import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Entity } from "models/entity";
import { useToggle } from "usehooks-ts";

import useNotification from "components/Notification/useNotification";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { SK_CHANNEL } from "hooks/useActionCable";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useChannel } from "hooks/useChannel";
import { useEffectOnce } from "hooks/useEffectOnce";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher, fetchApi } from "utils/baseFetcher";
import { primerInsightsUrl } from "utils/primerInsightsUrl";

import PrimerCountdown, { COUNTDOWN_SECONDS } from "./PrimerCountdown";
import PrimerRequestLoader from "./PrimerRequestLoader";

const InsightModal = dynamic(() => import("components/InsightModal"));

export function usePrimer({
  entity,
  tab,
  insightSwr,
}: {
  entity: Entity;
  tab?: string;
  insightSwr: { isValidating: boolean; data?: any; mutate: () => void };
}) {
  const { isValidating, data: insights, mutate: refreshInsights } = insightSwr;

  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { toggleModal } = useSignInSignUp();
  const { authenticated, account, accountId } = useAuthentication();
  const { setNotification } = useNotification();
  const { trackActivity } = useActivityTracker();

  const [showStripeModal, toggleStripeModal] = useToggle();
  const [isRequestingPrimer, setRequestingPrimer] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [freshPrimerInsight, setFreshPrimerInsight] = useState<any>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const primerSessionKey = entity?.id ? `primer-requesting-${entity.id}` : null;

  const setPrimerRequesting = useCallback(
    (value: boolean) => {
      setRequestingPrimer(value);
      if (!primerSessionKey) return;
      if (value) {
        sessionStorage.setItem(primerSessionKey, "true");
      } else {
        sessionStorage.removeItem(primerSessionKey);
      }
    },
    [primerSessionKey]
  );

  // On mount, restore loader state if primer was already being generated
  useEffect(() => {
    if (!primerSessionKey) return;
    if (sessionStorage.getItem(primerSessionKey) === "true") {
      setRequestingPrimer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primerSessionKey]);

  const cancelCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  }, []);

  const startCountdown = useCallback(
    (onComplete: () => void) => {
      if (countdown !== null) return;

      setCountdown(COUNTDOWN_SECONDS);
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null) return null;
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            countdownRef.current = null;
            setCountdown(null);
            onComplete();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [countdown]
  );

  const sendPrimerRequest = useCallback(async () => {
    setPrimerRequesting(true);

    try {
      const response = await fetchApi("/api/v3/request-insight-ai-primer", {
        method: "POST",
        body: JSON.stringify({
          "entity-id": entity?.id,
        }),
      });

      if (response.errors) {
        if (response?.errors?.[0].title === "Record conflict") {
          router.replace(
            {
              pathname: `/entities/${entity.slug}/research`,
              query: {
                ...router.query,
                tab: "primer",
                "open-primer": true,
              },
            },
            undefined,
            {
              shallow: true,
              scroll: false,
            }
          );
        } else if (
          response?.errors?.[0].title === "Request is already being processed"
        ) {
          // Keep the loader visible — the primer is already being generated
          return;
        } else {
          setNotification(
            `Unable to create request${
              response?.errors?.[0].title
                ? `, ${response?.errors?.[0].title}`
                : ""
            }`,
            "error"
          );
        }
        setPrimerRequesting(false);
        return;
      }

      trackActivity("aiprimer.request", entity);
    } catch (error) {
      setNotification("Unable to create request", "error");
      setPrimerRequesting(false);
    }
  }, [entity, router, setNotification, setPrimerRequesting, trackActivity]);

  // Returns true if there is a primer insight published within the last year
  const hasFreshPrimer = useMemo(() => {
    const allInsights =
      insights?.flatMap((page: any) => page?.data ?? []) ?? [];
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return allInsights.some(
      (insight: any) =>
        insight?.isPrimer && new Date(insight?.publishedAt) > oneYearAgo
    );
  }, [insights]);

  // If the primer finished while the user was on another page, clear the
  // sessionStorage key and stop the loader as soon as insights load with a fresh primer
  useEffect(() => {
    if (!primerSessionKey) return;
    if (sessionStorage.getItem(primerSessionKey) !== "true") return;
    if (!hasFreshPrimer) return;

    setPrimerRequesting(false);
    refreshInsights();
  }, [hasFreshPrimer, primerSessionKey, refreshInsights, setPrimerRequesting]);

  const onRequestPrimer = useCallback(
    async ({ userInitiated = false }: { userInitiated?: boolean } = {}) => {
      // Also check sessionStorage directly — state update from the restore effect
      // may not have propagated yet when this is called on initial mount
      const restoredFromSession =
        !!primerSessionKey &&
        sessionStorage.getItem(primerSessionKey) === "true";
      if (isRequestingPrimer || restoredFromSession || countdown !== null)
        return;
      if (entity?.shouldHidePrimer) return;

      if (!entity?.id) {
        setNotification("Unable to create request", "error");
      }

      if (!account?.canRequestAIPrimer) {
        if (!authenticated) {
          toggleModal("sign-up");
        } else {
          toggleStripeModal();
        }
        return;
      }

      // For explicit user action, fetch latest primer directly before requesting
      // a new one so we always respect the freshest published primer state.
      if (userInitiated && entity?.slug) {
        try {
          const data = await baseFetcher(primerInsightsUrl(entity.slug));
          const latestPrimer = data?.data?.[0];
          if (latestPrimer?.slug && latestPrimer?.publishedAt) {
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            if (new Date(latestPrimer.publishedAt) > oneYearAgo) {
              if (mobile) {
                router.push(`/insights/${latestPrimer.slug}`);
              } else {
                setFreshPrimerInsight(latestPrimer);
              }
              return;
            }
          }
        } catch (error) {
          // Fall through and continue with primer request behavior.
        }
      }

      if (userInitiated) {
        sendPrimerRequest();
      } else {
        startCountdown(sendPrimerRequest);
      }
    },
    [
      account?.canRequestAIPrimer,
      authenticated,
      countdown,
      entity?.id,
      entity?.slug,
      entity?.shouldHidePrimer,
      isRequestingPrimer,
      mobile,
      primerSessionKey,
      router,
      sendPrimerRequest,
      setNotification,
      startCountdown,
      toggleModal,
      toggleStripeModal,
    ]
  );
  const requestPrimerLoader = useMemo(() => {
    if (!isRequestingPrimer && countdown === null && !freshPrimerInsight)
      return;

    const isPrimerTab = tab === "primer";
    const isAllOrDefaultTab = !tab || tab === "all";
    const insightsList = insights?.flatMap((p: any) => p?.data ?? []) ?? [];
    const isEmptyList = insightsList.length === 0;
    const hasVisibleContent =
      freshPrimerInsight ||
      (isPrimerTab && (isRequestingPrimer || countdown !== null)) ||
      (isAllOrDefaultTab &&
        isEmptyList &&
        (isRequestingPrimer || countdown !== null));

    if (!hasVisibleContent) return;

    return (
      <>
        {freshPrimerInsight && (
          <InsightModal
            slug={freshPrimerInsight.slug}
            insight={freshPrimerInsight}
            isOpen={!!freshPrimerInsight}
            onClose={() => setFreshPrimerInsight(null)}
          />
        )}
        {(isRequestingPrimer || countdown !== null) &&
          (isPrimerTab || (isAllOrDefaultTab && isEmptyList)) && (
            <div className="flex flex-col items-center gap-[12px] px-[15px] md:px-[35px] pt-[32px] pb-[8px]">
              {countdown !== null ? (
                <PrimerCountdown
                  entityName={entity.prettyName}
                  countdown={countdown}
                  hasStalePrimer={
                    !!(insights?.flatMap((p: any) => p?.data ?? []) ?? []).some(
                      (i: any) => i?.isPrimer
                    ) && !hasFreshPrimer
                  }
                  onCancel={cancelCountdown}
                />
              ) : (
                <>
                  {!insights?.[0]?.data?.length && (
                    <div className="w-full max-w-[700px] rounded-xl border border-smart-teal/20 bg-smart-teal/5 px-[20px] py-[14px] text-[14px] text-slate-600 leading-relaxed">
                      Please wait while we put together a Smartkarma Primer on{" "}
                      <span className="font-medium text-smart-teal">
                        {entity.prettyName}
                      </span>{" "}
                      to help you get started. The Primer will provide a clear
                      foundation on the company, along with useful context and
                      directional insights as you begin exploring the
                      opportunity.
                    </div>
                  )}
                  <PrimerRequestLoader />
                </>
              )}
            </div>
          )}
      </>
    );
  }, [
    cancelCountdown,
    countdown,
    entity,
    freshPrimerInsight,
    hasFreshPrimer,
    insights,
    isRequestingPrimer,
    tab,
  ]);

  useEffectOnce(
    (ack) => {
      if (isValidating || !insights?.[0]) return;

      ack();

      if (tab || !insights?.[0] || insights?.[0]?.data?.length) return;

      console.log("request primer for empty insights list");
      onRequestPrimer();
    },
    [isValidating, insights, tab]
  );

  useEffectOnce(
    (ack) => {
      if (isValidating || !insights?.[0]) return;
      if (tab !== "primer") return;

      ack();

      const allInsights = insights.flatMap((page: any) => page?.data ?? []);
      const hasAnyPrimer = allInsights.some(
        (insight: any) => insight?.isPrimer
      );

      // Primer tab with no primer at all, or only stale (>1 year) primers → start countdown
      // Skip here if request-primer=true is present — that useEffect will handle it without countdown
      if (!hasAnyPrimer || !hasFreshPrimer) {
        if (router.query["request-primer"] === "true") return;
        onRequestPrimer();
      }
    },
    [hasFreshPrimer, insights, isValidating, onRequestPrimer, router, tab]
  );

  useEffect(() => {
    if (
      tab === "primer" &&
      router.query["request-primer"] === "true" &&
      !router.query["open-primer"]
    ) {
      if (isValidating || !insights?.[0]) return;

      router.replace(
        {
          query: { slug: router.query.slug, tab: router.query.tab },
        },
        undefined,
        { shallow: true, scroll: false }
      );

      if (isRequestingPrimer) {
        return;
      }

      onRequestPrimer({ userInitiated: true });
    }
  }, [
    hasFreshPrimer,
    insights,
    isRequestingPrimer,
    isValidating,
    onRequestPrimer,
    router,
    tab,
  ]);

  useChannel({
    key: "EntityPagePrimer",
    name: `private-account-${accountId}`,
    channel: {
      channel: SK_CHANNEL,
      room: `private-account-${accountId}`,
    },
    onReceived: async (message: any) => {
      const { event, notification_id: notificationId } = message;
      if (
        isRequestingPrimer &&
        event === "new-notification" &&
        notificationId
      ) {
        const notification = await fetchApi(
          `/api/v2/notifications/${notificationId}`
        );
        if (notification.key === "insight.ai_primer") {
          setTimeout(() => {
            setPrimerRequesting(false);
            // only refresh insight list when insights tab is all or primer
            if (tab === "primer" || !tab) {
              refreshInsights();
            }
          }, 500); // delay briefly to ensure the primer insight is available from the API before refreshing
        }
      }
    },
  });

  return {
    showStripeModal,
    toggleStripeModal,
    isRequestingPrimer,
    isCountingDown: countdown !== null,
    onRequestPrimer,
    requestPrimerLoader,
  };
}
